/**
 * Query-key shape (and the page-size / filter constants baked into it) for the
 * profile author-feed infinite query in `usePosts`.
 *
 * This lives in its own module, rather than inline at the `useInfiniteQuery` call,
 * because the pinned-post commit confirmation in `usePostEngagement` writes its
 * confirmed page straight into *this* query's cache with `setQueryData`. An
 * exact-key write silently no-ops the moment two hand-spelled keys drift apart,
 * and a silent no-op here reinstates the stale-feed bug the confirmation exists
 * to fix — so both sides build the key from one function instead.
 */
import type { QueryKey } from '@tanstack/react-query'
import type { AppView } from '@/contexts/AppViewContext'
import type { AuthorFeedFilter } from '@/types/posts'

export type PostsFeedSource = 'collection' | 'authorFeed'

/** Default page size for the posts infinite query (and so for the confirmation poll). */
export const PROFILE_FEED_PAGE_SIZE = 25

/**
 * The only author-feed filter the AppView returns a pinned (`reasonPin`) row for,
 * and therefore the only feed a pin/unpin is observable in. Pin state is *unknown*
 * on every other filter, which is what keeps the Pin/Unpin action off them.
 */
export const PINNED_POST_FEED_FILTER: AuthorFeedFilter = 'POSTS_NO_REPLIES'

export interface PostsQueryKeyParams {
    identifier: string | undefined
    feedSource: PostsFeedSource
    authorFeedFilter: AuthorFeedFilter | undefined
    /** Whether a Bluesky OAuth session is present — viewer state changes the page contents. */
    hasSession: boolean
    hasPixelfedSession: boolean
    appView: AppView
}

export function postsQueryKey({
    identifier,
    feedSource,
    authorFeedFilter,
    hasSession,
    hasPixelfedSession,
    appView,
}: PostsQueryKeyParams): QueryKey {
    return [
        'posts',
        identifier,
        feedSource,
        authorFeedFilter,
        hasSession,
        hasPixelfedSession,
        appView,
    ]
}
